/**
 * Pinned "Home" — not one of the FSD's numbered parts (docs §19161): the
 * daily-use shared-work screens that sit above all of them. One section,
 * `workspace`, with no `kind` (it is not an FSD section); each item carries
 * the Part D cross-cutting specification it implements — D.9.2 approvals,
 * D.4 notifications and the attention list, D.7 the operating-day board.
 * The dashboard implements no FSD section and carries no `fsd`.
 *
 * Item ids are stable: favourites and recents are keyed off `id` in
 * localStorage, not off section or position.
 */
import type { NavGroup } from '../types'
import { PERMISSIONS } from '@/lib/permissions'
import { ATTENTION_PERMISSIONS } from '@/modules/attention/permissions'
import { NOTIFICATIONS_PERMISSIONS } from '@/modules/notifications/permissions'
import { OPCAL_PERMISSIONS } from '@/modules/opcal/permissions'

export const HOME_GROUPS: NavGroup[] = [
  {
    id: 'workspace',
    labelKey: 'nav.groups.workspace',
    items: [
      {
        id: 'dashboard',
        labelKey: 'nav.items.dashboard.label',
        to: '/',
        icon: 'dashboard',
        keywordsKey: 'nav.items.dashboard.keywords',
        descriptionKey: 'nav.items.dashboard.description',
      },
      {
        id: 'approvals',
        labelKey: 'nav.items.approvals.label',
        to: '/approvals',
        icon: 'inbox',
        permission: PERMISSIONS.approvalAct,
        keywordsKey: 'nav.items.approvals.keywords',
        descriptionKey: 'nav.items.approvals.description',
        fsd: 'D.9.2',
        code: 'XFN-F02',
        // Restores the 9.2.8 reference C15 carried in description copy (removed in C17).
        related: ['9.2.8'],
      },
      // C1 (design §10): the unified attention list (XFN-NT-001). Third, after
      // Dashboard and Approvals — daily-use, shared work, not any one
      // module's screen.
      {
        id: 'attention',
        labelKey: 'attention.routeTitles.list',
        to: '/attention',
        icon: 'alert',
        permission: ATTENTION_PERMISSIONS.itemRead,
        descriptionKey: 'nav.items.attention.description',
        fsd: 'D.4',
        code: 'XFN-NT-001',
      },
      // The notification inbox — the history behind the bell in the command
      // bar (FSD D.4). It sits beside Attention rather than under Settings
      // because it is read, not configured; `/settings/notification-policies`
      // is the configuration screen.
      {
        id: 'notifications',
        labelKey: 'notifications.routeTitles.inbox',
        to: '/notifications',
        icon: 'mail',
        permission: NOTIFICATIONS_PERMISSIONS.read,
        keywordsKey: 'notifications.nav.keywords',
        descriptionKey: 'notifications.nav.description',
        fsd: 'D.4',
      },
      // C1 (design §7): the business-day board. Worked every evening, so it
      // belongs beside Dashboard and Approvals rather than with the calendar
      // it reads (Part 1 catalogue).
      {
        id: 'opcal-day-board',
        labelKey: 'opcal.routeTitles.board',
        to: '/operations/day-board',
        icon: 'clock',
        permission: OPCAL_PERMISSIONS.dayRead,
        descriptionKey: 'nav.items.opcalDayBoard.description',
        fsd: 'D.7',
        code: 'XFN-OP-004',
      },
    ],
  },
]
